// Master-P3 Wave 6.4 — Performance-Freshness Chip.
//
// Renders a compact indicator that tells the user, at a glance,
// whether a KPI card is backed by real data, synthetic data, or an
// unknown-provenance import.
//
// Usage:
//   <PerformanceFreshnessChip data={{ data_source, is_synthetic,
//                                       imported_at, environment }} />
//
// Rendered variants:
//   [echt · Stand 14:23]   — data_source=real, freshness from imported_at
//   [simuliert]            — is_synthetic=true OR data_source=synthetic
//   [unbekannt]            — data_source=unknown / null
//   [preview]              — environment=preview (superimposed on any tone)
import React from "react";
import { Badge } from "../ui/badge";
import { Clock, Sparkles, HelpCircle, Beaker } from "lucide-react";

function fmtHHMM(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return `${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes()).padStart(2, "0")}`;
  } catch (_) { return ""; }
}

export function PerformanceFreshnessChip({ data, testId }) {
  if (!data) return null;
  const {
    data_source, is_synthetic, imported_at, environment,
  } = data;

  const isPreview = (environment || "").toLowerCase() === "preview";
  const isSynthetic = is_synthetic === true
    || data_source === "synthetic";
  const isUnknown = !data_source || data_source === "unknown";
  const isReal = data_source === "real" && !isSynthetic;

  let tone = "bg-slate-100 text-slate-700 border-0";
  let label = "unbekannt";
  let Icon = HelpCircle;

  if (isReal) {
    tone = "bg-emerald-100 text-emerald-800 border-0";
    const hh = fmtHHMM(imported_at);
    label = hh ? `echt · Stand ${hh}` : "echt";
    Icon = Clock;
  } else if (isSynthetic) {
    tone = "bg-fuchsia-100 text-fuchsia-800 border-0";
    label = "simuliert";
    Icon = Sparkles;
  } else if (isUnknown) {
    label = "unbekannt";
    Icon = HelpCircle;
  }

  return (
    <span className="inline-flex items-center gap-1"
          data-testid={testId || "perf-freshness-chip"}>
      <Badge className={tone}>
        <Icon className="h-3 w-3 mr-1 inline" />
        {label}
      </Badge>
      {isPreview && (
        <Badge className="bg-amber-100 text-amber-800 border-0"
               data-testid={`${testId || "perf-freshness-chip"}-preview`}>
          <Beaker className="h-3 w-3 mr-1 inline" />
          preview
        </Badge>
      )}
    </span>
  );
}

export default PerformanceFreshnessChip;
